import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";

const LocalApiUrl = "/api";

const COVER_COLORS = [
  "#1C1916", "#1A1C1E", "#181C1A", "#1C1A1E", "#1E1A18",
  "#1A1E1C", "#1C1E1A", "#1E1C1A",
];
function coverColor(title = "") {
  let h = 0;
  for (let i = 0; i < title.length; i++) h = (h * 31 + title.charCodeAt(i)) >>> 0;
  return COVER_COLORS[h % COVER_COLORS.length];
}

function WishlistCard({ book, onRemove }) {
  const [hovered, setHovered] = useState(false);
  const initial = (book.title || "?")[0].toUpperCase();

  return (
    <div
      className="group relative flex flex-col bg-ink-surface rounded-xl overflow-hidden
                 border border-ink-border hover:border-ink-muted transition-all duration-200"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Cover */}
      <div
        className="relative w-full aspect-[2/3] flex items-center justify-center overflow-hidden flex-shrink-0"
        style={{ backgroundColor: coverColor(book.title) }}
      >
        {book.imageLink
          ? <img src={book.imageLink} alt={book.title} className="w-full h-full object-cover" />
          : <span className="text-4xl font-bold text-ink-muted select-none">{initial}</span>
        }

        {/* Hover overlay */}
        <div className={`absolute inset-0 bg-ink-base/80 flex items-center justify-center
                         transition-opacity duration-200 ${hovered ? 'opacity-100' : 'opacity-0'}`}>
          <button
            onClick={() => onRemove(book)}
            className="text-xs font-medium text-crimson border border-crimson/30 px-4 py-1.5
                       rounded-md hover:bg-crimson/10 transition-colors"
          >
            Remove
          </button>
        </div>
      </div>

      {/* Info */}
      <div className="p-3 flex flex-col gap-0.5">
        <p className="text-cream text-xs font-medium leading-snug line-clamp-2">{book.title}</p>
        <p className="text-cream-muted text-[11px] leading-snug line-clamp-1">{book.author}</p>
        {book.release_year && (
          <p className="text-cream-muted text-[10px] mt-0.5">{book.release_year}</p>
        )}
      </div>
    </div>
  );
}

function WishList() {
  const [books,   setBooks]   = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchWishlist(); }, []);

  const fetchWishlist = async () => {
    setLoading(true);
    try {
      const userId = localStorage.getItem("userId");
      const res = await axios.get(`${LocalApiUrl}/books/${userId}`);
      const all = Array.isArray(res.data?.books) ? res.data.books
                : Array.isArray(res.data)         ? res.data
                : [];
      setBooks(
        all.filter(b => b.wishlist === true)
           .sort((a, b) => new Date(b.addedOn) - new Date(a.addedOn))
      );
    } catch (err) {
      console.error("fetchWishlist:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (book) => {
    try {
      await axios.post(`/api/books/delete`, { id: book._id });
      setBooks(prev => prev.filter(b => b._id !== book._id));
    } catch (err) {
      console.error("remove:", err);
    }
  };

  return (
    <div className="min-h-screen bg-ink-base">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10">

        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-bold text-cream tracking-tight">Wishlist</h1>
            <p className="text-cream-muted text-sm mt-0.5">
              {loading ? "Loading…" : `${books.length} book${books.length !== 1 ? "s" : ""} saved`}
            </p>
          </div>
          <Link to="/addbook" className="sc-btn-ghost text-xs py-1.5 px-4 flex-shrink-0">
            My Library
          </Link>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="bg-ink-surface rounded-xl border border-ink-border aspect-[2/3] animate-pulse" />
            ))}
          </div>
        ) : books.length === 0 ? (
          <div className="py-32 text-center">
            <p className="text-cream text-lg font-semibold mb-1">Nothing saved yet</p>
            <p className="text-cream-muted text-sm mb-6">
              When you save a book for later, it lives here.
            </p>
            <Link to="/addbook" className="sc-btn-gold text-sm">
              Go to your library
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {books.map((book, i) => (
              <WishlistCard key={book._id || i} book={book} onRemove={handleRemove} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default WishList;
